import type { AssistantReportPreviewRow } from "../entities/assistant";

type SortDirection = "asc" | "desc";
type PreviewCellValue = AssistantReportPreviewRow["cells"][string];

const DATE_LIKE_PATTERN =
  /^(?:\d{4}-\d{2}-\d{2}(?:[T\s].*)?|\d{1,2}\/\d{1,2}\/\d{2,4}|[A-Za-z]{3,9}\s+\d{1,2},?\s+\d{4})$/;

const isBlankPreviewValue = (value: PreviewCellValue | undefined): boolean =>
  value === null || value === undefined || (typeof value === "string" && value.trim() === "");

const parsePreviewNumber = (value: PreviewCellValue): number | null => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : null;
  }
  if (typeof value !== "string") {
    return null;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return null;
  }

  const parenthesized = /^\((.*)\)$/.exec(trimmed);
  const rawBody = parenthesized ? parenthesized[1] : trimmed;
  const normalized = rawBody
    .replace(/[\u0024\u20AC\u00A3\u00A5,\s]/g, "")
    .replace(/%$/, "");
  if (!/^[+-]?(?:\d+(?:\.\d+)?|\.\d+)$/.test(normalized)) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return null;
  }
  return parenthesized ? -parsed : parsed;
};

const parsePreviewDateMs = (value: PreviewCellValue): number | null => {
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  if (!DATE_LIKE_PATTERN.test(trimmed)) {
    return null;
  }
  const parsed = Date.parse(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
};

const comparePresentPreviewValues = (
  left: PreviewCellValue,
  right: PreviewCellValue,
): number => {
  if (typeof left === "boolean" && typeof right === "boolean") {
    return Number(left) - Number(right);
  }

  const leftNumber = parsePreviewNumber(left);
  const rightNumber = parsePreviewNumber(right);
  if (leftNumber !== null && rightNumber !== null) {
    return leftNumber - rightNumber;
  }

  const leftDateMs = parsePreviewDateMs(left);
  const rightDateMs = parsePreviewDateMs(right);
  if (leftDateMs !== null && rightDateMs !== null) {
    return leftDateMs - rightDateMs;
  }

  return String(left).localeCompare(String(right), undefined, {
    numeric: true,
    sensitivity: "base",
  });
};

export const compareReportGenerationPreviewValues = (
  left: PreviewCellValue | undefined,
  right: PreviewCellValue | undefined,
  direction: SortDirection,
): number => {
  const leftBlank = isBlankPreviewValue(left);
  const rightBlank = isBlankPreviewValue(right);
  if (leftBlank && rightBlank) {
    return 0;
  }
  if (leftBlank) {
    return 1;
  }
  if (rightBlank) {
    return -1;
  }

  const comparison = comparePresentPreviewValues(
    left as PreviewCellValue,
    right as PreviewCellValue,
  );
  return direction === "asc" ? comparison : -comparison;
};

export const sortReportGenerationPreviewRows = (
  rows: readonly AssistantReportPreviewRow[],
  sortColumn: string | null,
  sortDirection: SortDirection | null,
): AssistantReportPreviewRow[] => {
  if (!sortColumn || !sortDirection) {
    return [...rows];
  }

  return [...rows].sort((left, right) =>
    compareReportGenerationPreviewValues(
      left.cells[sortColumn],
      right.cells[sortColumn],
      sortDirection,
    ),
  );
};
